import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, Box, Typography, Stack, Chip, CircularProgress,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axios from 'axios';

const PRESETS = [50, 100, 250, 500];

/**
 * Admin wallet adjustment.
 *
 * Wallets are otherwise only credited by a verified payment. This is the one
 * manual path — deliberately admin-only, requiring a reason, and recorded as a
 * Transaction so every adjustment is attributable after the fact.
 *
 * A negative amount debits (for corrections); the server refuses to push a
 * balance below zero.
 */
export default function AdminWalletCredit({ open, user, onClose, onDone }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const reset = () => {
    setAmount(''); setReason(''); setError(''); setResult(null); setLoading(false);
  };

  const close = () => { reset(); onClose?.(); };

  const submit = async () => {
    setError('');
    const value = Number(amount);
    if (!Number.isFinite(value) || value === 0) {
      setError('Enter a non-zero amount (use a negative number to debit).');
      return;
    }
    if (reason.trim().length < 3) {
      setError('A reason is required — it is stored on the audit record.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `/api/users/${user._id || user.id}/wallet`,
        { tokenAmount: value, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(data.data);
      onDone?.();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Adjustment failed');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <AccountBalanceWalletIcon color="primary" />
        Adjust wallet
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {user.email}
        </Typography>

        {result ? (
          <Alert severity="success" sx={{ borderRadius: '12px' }}>
            <Typography variant="body2" fontWeight={700}>
              {result.adjustment > 0 ? 'Credited' : 'Debited'} {Math.abs(result.adjustment)} tokens
            </Typography>
            <Typography variant="body2">
              Balance {result.previousBalance} → <strong>{result.tokenBalance}</strong>
            </Typography>
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              {PRESETS.map((p) => (
                <Chip
                  key={p}
                  label={`+${p}`}
                  onClick={() => setAmount(String(p))}
                  variant={String(p) === amount ? 'filled' : 'outlined'}
                  color="primary"
                />
              ))}
            </Stack>

            <TextField
              fullWidth
              label="Amount (tokens)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 500  — negative to debit"
              inputMode="numeric"
              size="small"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. launch promo, support refund, testing"
              size="small"
              multiline
              minRows={2}
            />
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Recorded as a transaction against your admin account.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            onClick={submit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Applying…' : 'Apply adjustment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

AdminWalletCredit.propTypes = {
  open: PropTypes.bool,
  user: PropTypes.object,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
};
